import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers';
import { withTargetChecked } from '../../../utils';
import DropdownButton from '../../generic/DropdownButton';
import { FormLabel, FormLabelVariant, LabeledCheckbox, NumberInput } from '../../generic/Forms';
import Tooltip from '../../generic/Tooltip';
import type { UpdateHookScheduledInterval } from '../../../../shared/model-typings';

import Section from './SettingsPageSection';
import DAY_BY_HALF_HOUR_OPTIONS, { valuesToOptions } from './updateHookDefaultScheduledIntervalTime';

import styles from './Settings.module.less';

type Props = {
  isLoading: boolean;
  isUpdateHooksEnabled: boolean;
  onToggleIsUpdateHooksEnabled: (isEnabled: boolean) => void;
  scheduledInterval: UpdateHookScheduledInterval | null;
  onScheduledIntervalChange: (scheduledInterval: UpdateHookScheduledInterval) => void;
  scheduledIntervalLength: number | null;
  onScheduledIntervalLengthChange: (scheduledIntervalLength: number) => void;
  scheduledIntervalTime: string | null;
  onScheduledIntervalTimeChange: (scheduledIntervalTime: string) => void;
};

const defaultProjectUpdateHookSettings = {
  updateHookDefaultScheduledEnabled: false,
  updateHookDefaultScheduledInterval: 'day',
  updateHookDefaultScheduledIntervalLength: 2,
  updateHookDefaultScheduledIntervalTime: '12:00AM',
};

/**
 * ProjectUpdateHookSettings is the generic presentational component
 * for the project update hook settings. It is used in the organization settings
 * and in the project settings.
 */
const ProjectUpdateHookSettings: VoidFunctionComponent<Props> = ({
  isLoading,
  isUpdateHooksEnabled,
  onToggleIsUpdateHooksEnabled,
  scheduledInterval,
  onScheduledIntervalChange,
  scheduledIntervalLength,
  onScheduledIntervalLengthChange,
  scheduledIntervalTime,
  onScheduledIntervalTimeChange,
}) => {
  const scanFrequencyText = plural(
    scheduledIntervalLength || defaultProjectUpdateHookSettings.updateHookDefaultScheduledIntervalLength,
    _.upperFirst(scheduledInterval || defaultProjectUpdateHookSettings.updateHookDefaultScheduledInterval),
  );

  const validateIntervalLength = (nextScheduledIntervalLength: string) => {
    const intervalLengthAsNumber = _.toInteger(nextScheduledIntervalLength);
    if (intervalLengthAsNumber <= 0) {
      return;
    }

    onScheduledIntervalLengthChange(intervalLengthAsNumber);
  };

  return (
    <>
      <LabeledCheckbox
        label="Enable update hooks"
        isChecked={isUpdateHooksEnabled}
        onChange={withTargetChecked(onToggleIsUpdateHooksEnabled)}
        isDisabled={isLoading}
      />
      <Section.Indented isDisabled={!isUpdateHooksEnabled}>
        <FormLabel label="Update every" variant={FormLabelVariant.Filter}>
          <div className={styles.updateHooksInput}>
            <NumberInput
              label="update-cadence"
              onChange={validateIntervalLength}
              value={_.toInteger(scheduledIntervalLength) ||
                defaultProjectUpdateHookSettings.updateHookDefaultScheduledIntervalLength}
              size="small"
              isDisabled={isLoading || !isUpdateHooksEnabled}
            />
            <DropdownButton
              id="settings-project-scan-frequency"
              title={scanFrequencyText}
              disabled={isLoading || !isUpdateHooksEnabled}
              onSelect={onScheduledIntervalChange}
            >
              <DropdownButton.Item
                key="hour"
                eventKey="hour"
                active={scheduledInterval === 'hour'}
              >
                Hour(s)
              </DropdownButton.Item>
              <DropdownButton.Item
                key="day"
                eventKey="day"
                active={scheduledInterval === 'day'}
              >
                Day(s)
              </DropdownButton.Item>
              <DropdownButton.Item
                key="week"
                eventKey="week"
                active={scheduledInterval === 'week'}
              >
                Week(s)
              </DropdownButton.Item>
            </DropdownButton>
          </div>
        </FormLabel>
        <FormLabel label="Update time" variant={FormLabelVariant.Filter}>
          <Tooltip
            id="project-scan-update-time-tooltip"
            tooltip="You can only set the update time if the update frequency is set to daily."
            showTooltip={scheduledInterval !== 'day'}
            placement="right"
          >
            <div className="inline-block">
              <DropdownButton
                id="settings-project-scan-update-time"
                title={(valuesToOptions[scheduledIntervalTime as string]?.label ??
                  defaultProjectUpdateHookSettings.updateHookDefaultScheduledIntervalTime)}
                className={styles.updateHooksDropdownMenu}
                onSelect={onScheduledIntervalTimeChange}
                disabled={isLoading || !isUpdateHooksEnabled || scheduledInterval !== 'day'}
              >
                {DAY_BY_HALF_HOUR_OPTIONS.map(({ value, label }) => (
                  <DropdownButton.Item
                    key={value}
                    eventKey={value}
                    active={scheduledIntervalTime === null
                      ? value === '00:00:00'
                      : value === scheduledIntervalTime}
                  >
                    {label}
                  </DropdownButton.Item>
                ))}
              </DropdownButton>
            </div>
          </Tooltip>
        </FormLabel>
        <Section.Paragraph>
          Time zone is Pacific time (PT)
        </Section.Paragraph>
      </Section.Indented>
    </>
  );
};

export default ProjectUpdateHookSettings;
